import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from "@fleet-os/api-client";

const TEAM_API = `${API_BASE_URL}/api/team`;

function AzureGroupMembers({ groupId, role }) {
  const [azureMembers, setAzureMembers] = useState([]);
  const [isAzureLoading, setIsAzureLoading] = useState(false);

  useEffect(() => {
    const fetchAzureMembers = async () => {
      try {
        setIsAzureLoading(true);
        const res = await fetch(`${TEAM_API}/azure-group/${groupId}`);
        if (res.ok) {
          const data = await res.json();
          setAzureMembers(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsAzureLoading(false);
      }
    };
    fetchAzureMembers();
  }, [groupId]);

  return (
    <div style={{ marginTop: '16px', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px' }}>
      <h5 style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '12px' }}>Active Members in Azure AD:</h5>
      {isAzureLoading ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading from Azure...</div>
      ) : azureMembers.length > 0 ? (
        <table className="isd-table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>GRANTED ROLE</th>
            </tr>
          </thead>
          <tbody>
            {azureMembers.map(m => (
              <tr key={m.id}>
                <td style={{ fontWeight: 600 }}>{m.name || '-'}</td>
                <td>{m.email}</td>
                <td>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem', 
                    background: role === 'Admin' ? 'rgba(239, 68, 68, 0.15)' : role === 'Manager' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    color: role === 'Admin' ? '#fca5a5' : role === 'Manager' ? '#93c5fd' : '#d1d5db'
                  }}>
                    {role}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No members found or missing Graph API permissions.</div>
      )}
    </div>
  );
}

function DashboardSSOMappings({ apiBase, title = "SSO Role Mappings", baseGroupId }) {
  const [mappings, setMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newGroupId, setNewGroupId] = useState('');
  const [newRole, setNewRole] = useState('Viewer');

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${apiBase}/mappings`);
      if (res.ok) {
        const data = await res.json();
        setMappings(data);
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addMapping = async () => {
    if (!newGroupId.trim()) return;
    try {
      await fetch(`${apiBase}/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: newGroupId.trim(), role: newRole })
      });
      setNewGroupId('');
      fetchMappings();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteMapping = async (id) => {
    try {
      await fetch(`${apiBase}/mappings/${id}`, { method: 'DELETE' });
      fetchMappings();
    } catch (err) {
      console.error(err);
    }
  };

  const updateMappingRole = async (id, groupId, groupName, role) => {
    try {
      await fetch(`${apiBase}/mappings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId, group_name: groupName, role })
      });
      fetchMappings();
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) return <div style={{ color: 'var(--text-muted)', padding: 20 }}>Loading mappings...</div>;

  return (
    <div className="isd-card" style={{ marginBottom: 20 }}>
      <div className="isd-card-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span className="isd-card-title">{title}</span>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Map Microsoft Entra security groups to localized dashboard roles.
        </p>
      </div>
      <div className="isd-card-body">
        
        {baseGroupId && (
          <div style={{ background: 'var(--bg-primary)', padding: 16, borderRadius: 8, marginBottom: 24, border: '1px solid var(--border-color)', borderLeft: '4px solid var(--accent-emerald, #10b981)' }}>
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: '0 0 4px', fontSize: '1rem', color: 'var(--text-primary)' }}>Default Base Group (Hardcoded)</h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Users in this group are automatically granted access to the dashboard.</p>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input 
                type="text" 
                value={baseGroupId} 
                readOnly 
                style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', padding: '8px 12px', borderRadius: 6 }}
              />
              <div style={{ width: 120, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '8px', borderRadius: 6, textAlign: 'center', fontWeight: 600, fontSize: '0.85rem' }}>
                Admin
              </div>
            </div>
            <AzureGroupMembers groupId={baseGroupId} role="Admin" />
          </div>
        )}

        {mappings.map(map => (
          <div key={map.id} style={{ background: 'var(--bg-primary)', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input 
                type="text" 
                value={map.group_id} 
                readOnly 
                style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', padding: '8px 12px', borderRadius: 6 }}
              />
              <select 
                value={map.role} 
                onChange={(e) => updateMappingRole(map.id, map.group_id, map.group_name, e.target.value)}
                style={{ width: 120, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '8px', borderRadius: 6 }}
              >
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="Viewer">Viewer</option>
              </select>
              <button onClick={() => deleteMapping(map.id)} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>
                Remove
              </button>
            </div>
            <AzureGroupMembers groupId={map.group_id} role={map.role} />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, padding: 16, borderTop: '1px solid var(--border-color)' }}>
          <input 
            type="text" 
            placeholder="Enter Entra Group Object ID" 
            value={newGroupId}
            onChange={(e) => setNewGroupId(e.target.value)}
            style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '8px 12px', borderRadius: 6 }}
          />
          <select 
            value={newRole} 
            onChange={(e) => setNewRole(e.target.value)}
            style={{ width: 120, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '8px', borderRadius: 6 }}
          >
            <option value="Admin">Admin</option>
            <option value="Manager">Manager</option>
            <option value="Viewer">Viewer</option>
          </select>
          <button onClick={addMapping} disabled={!newGroupId.trim()} style={{ background: 'var(--accent-emerald, #10b981)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: newGroupId.trim() ? 'pointer' : 'not-allowed', opacity: newGroupId.trim() ? 1 : 0.5 }}>
            + Add Mapping
          </button>
        </div>

      </div>
    </div>
  );
}

export default DashboardSSOMappings;
